/**
 * RegulatorEditPanel — Modo Edición: corrige la posición GPS del regulador
 * seleccionado. Guardado directo desde 2026-09-10 (antes: el cambio quedaba
 * "en borrador" y exportaba un snippet que nunca llegaba a pegarse a mano —
 * ver ROADMAP.md). Cada acción persiste de inmediato vía `/api/overrides`
 * y queda auditada (`admin_action` en el histórico de eventos).
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, MapPin, Move, Link2, BellOff, Trash2, Navigation } from 'lucide-react';
import { useSelection } from '../../store/selection';
import { useEditMode } from '../../store/editMode';
import { usePositionOverridesDraft } from '../../store/positionOverridesDraft';
import { useOsmGroupOverridesDraft } from '../../store/osmGroupOverridesDraft';
import { useGroupBearingOverridesDraft } from '../../store/groupBearingOverridesDraft';
import { useAlarmMasks } from '../../store/alarmMasks';
import { useRegulatorLifecycleDraft } from '../../store/regulatorLifecycleDraft';
import { useOverridesVersion } from '../../store/overridesVersion';
import { positionOverrides, groupBearingOverrides } from '../../data/overridesRuntime';
import { santaColomaIntersections, santaColomaStaticSignals } from '../../data/santa-coloma';
import { osmGroupByCabinetIdResolved } from '../../data/osm-group-mapping-resolved';
import { toCanonicalId, toDisplayId } from '../../utils/canonicalId';
import { flyToPoint } from '../../utils/camera-api';

function fmt(n: number): string {
  return n.toFixed(6);
}

// Distancia aproximada en metros (equirectangular, suficiente a esta escala
// para una pista visual "¿este semáforo es del cruce correcto?").
function approxDistanceM(a: [number, number], b: [number, number]): number {
  const R = 6371000;
  const dLat = (b[1] - a[1]) * Math.PI / 180;
  const dLng = (b[0] - a[0]) * Math.PI / 180;
  const meanLat = (a[1] + b[1]) / 2 * Math.PI / 180;
  const x = dLng * Math.cos(meanLat);
  return Math.sqrt(x * x + dLat * dLat) * R;
}

export function RegulatorEditPanel() {
  // Se suscribe a la versión de overrides solo para re-renderizar cuando
  // algo se guarda — el dato en sí se lee directo de los arrays runtime.
  useOverridesVersion(s => s.version);

  const selectedId = useSelection(s => s.selectedId);
  const clearSelection = useSelection(s => s.clear);
  const picking = useEditMode(s => s.picking);
  const pendingCoord = useEditMode(s => s.pendingCoord);
  const startPicking = useEditMode(s => s.startPicking);
  const stopPicking = useEditMode(s => s.stopPicking);
  const clearPendingCoord = useEditMode(s => s.clearPendingCoord);
  const setPendingCoord = useEditMode(s => s.setPendingCoord);
  const stage = usePositionOverridesDraft(s => s.stage);
  const removePositionOverride = usePositionOverridesDraft(s => s.remove);
  const positionSaving = usePositionOverridesDraft(s => s.saving);
  const [moveCenter, setMoveCenter] = useState(true);
  const [saveError, setSaveError] = useState('');

  const [enteringCoords, setEnteringCoords] = useState(false);
  const [latInput, setLatInput] = useState('');
  const [lngInput, setLngInput] = useState('');
  const [coordError, setCoordError] = useState('');

  const pickingSignal = useEditMode(s => s.pickingSignal);
  const pendingSignalId = useEditMode(s => s.pendingSignalId);
  const startPickingSignal = useEditMode(s => s.startPickingSignal);
  const stopPickingSignal = useEditMode(s => s.stopPickingSignal);
  const clearPendingSignalId = useEditMode(s => s.clearPendingSignalId);
  const stageSignal = useOsmGroupOverridesDraft(s => s.stage);
  const excludeSignalWiring = useOsmGroupOverridesDraft(s => s.exclude);
  const wiringSaving = useOsmGroupOverridesDraft(s => s.saving);

  const stageBearing = useGroupBearingOverridesDraft(s => s.stage);
  const removeBearingOverride = useGroupBearingOverridesDraft(s => s.remove);
  const bearingSaving = useGroupBearingOverridesDraft(s => s.saving);

  const alarmMasks = useAlarmMasks(s => s.masks);
  const createAlarmMask = useAlarmMasks(s => s.createMask);
  const deleteAlarmMask = useAlarmMasks(s => s.deleteMask);
  const [maskFormOpen, setMaskFormOpen] = useState(false);
  const [maskNote, setMaskNote] = useState('');
  const [maskError, setMaskError] = useState('');

  const stageRemoveRegulator = useRegulatorLifecycleDraft(s => s.stageRemove);
  const lifecycleSaving = useRegulatorLifecycleDraft(s => s.saving);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Grupo activo en el grid de "Cablear grupos" — null hasta que el
  // operador elige uno; el JSX cae al primero del regulador por defecto.
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  useEffect(() => { setSelectedGroupId(null); }, [selectedId]);

  // Ajuste de rotación del grupo activo — mismo patrón submit-explícito que
  // "Coordenadas exactas", sin debounce.
  const [rotatingGroupId, setRotatingGroupId] = useState<number | null>(null);
  const [bearingInput, setBearingInput] = useState('');
  const [bearingError, setBearingError] = useState('');
  useEffect(() => { setRotatingGroupId(null); setBearingError(''); }, [selectedGroupId, selectedId]);

  // Esc cancela el modo picking activo (posición o semáforo), igual que en
  // el editor de polígono admin.
  useEffect(() => {
    if (!picking && !pickingSignal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      stopPicking();
      stopPickingSignal();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [picking, pickingSignal, stopPicking, stopPickingSignal]);

  const close = () => {
    clearPendingCoord();
    stopPicking();
    stopPickingSignal();
    clearPendingSignalId();
    setEnteringCoords(false);
    setMaskFormOpen(false);
    setMaskNote('');
    setMaskError('');
    setConfirmingDelete(false);
    setSaveError('');
    clearSelection();
  };

  if (selectedId === null) return null;
  const ix = santaColomaIntersections.find(i => i.cabinet.logicalId === selectedId);
  if (!ix) return null;

  const canonicalId = toCanonicalId(selectedId);
  const hasPositionOverride = positionOverrides.some(d => d.logicalId === selectedId);

  const deleteRegulator = async () => {
    setSaveError('');
    if (!(await stageRemoveRegulator(selectedId))) {
      setSaveError('No se pudo eliminar — reintenta');
      return;
    }
    close();
  };

  const confirmMove = async () => {
    if (!pendingCoord) return;
    setSaveError('');
    if (!(await stage({ logicalId: selectedId, cabinet: pendingCoord, moveCenter }))) {
      setSaveError('No se pudo guardar — reintenta');
      return;
    }
    if (moveCenter) flyToPoint(pendingCoord);
    clearPendingCoord();
  };

  const removePosition = async () => {
    setSaveError('');
    if (!(await removePositionOverride(selectedId))) setSaveError('No se pudo quitar — reintenta');
  };

  const submitCoords = () => {
    const lat = parseFloat(latInput);
    const lng = parseFloat(lngInput);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      setCoordError('Formato inválido — usa punto decimal, ej. 41.452768');
      return;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setCoordError('Fuera de rango — latitud [-90, 90], longitud [-180, 180]');
      return;
    }
    setCoordError('');
    setPendingCoord([lng, lat]);
    setEnteringCoords(false);
  };

  const pendingCoordDistance = pendingCoord
    ? approxDistanceM(pendingCoord, ix.cabinet.position)
    : null;

  const cabinetAssignments = osmGroupByCabinetIdResolved[selectedId] ?? [];
  const pendingSignal = pendingSignalId !== null
    ? santaColomaStaticSignals.find(s => s.id === pendingSignalId)
    : undefined;
  const pendingSignalDistance = pendingSignal
    ? approxDistanceM(pendingSignal.position, ix.cabinet.position)
    : null;
  const wiredGroupIds = new Set(cabinetAssignments.map(a => a.groupId));
  const cableadosCount = ix.groups.filter(g => wiredGroupIds.has(g.id)).length;
  const activeGroupId = selectedGroupId ?? ix.groups[0]?.id ?? null;
  const activeGroup = ix.groups.find(g => g.id === activeGroupId);
  const activeAssignments = cabinetAssignments.filter(a => a.groupId === activeGroupId);
  // Un regulador real puede controlar varios cruces (16-32 grupos, no solo
  // 4) — al elegir a qué grupo pertenece un semáforo recién cableado, listar
  // por cercanía es mucho más rápido que leer una lista larga en orden fijo.
  const groupsByDistance = pendingSignal
    ? [...ix.groups].sort((a, b) =>
        approxDistanceM(pendingSignal.position, a.position) - approxDistanceM(pendingSignal.position, b.position))
    : ix.groups;

  const confirmSignal = async (groupId: number) => {
    if (pendingSignalId === null) return;
    setSaveError('');
    if (!(await stageSignal({ osmId: pendingSignalId, cabinetId: selectedId, groupId }))) {
      setSaveError('No se pudo guardar — reintenta');
      return;
    }
    clearPendingSignalId();
  };

  const unassignSignal = async (osmId: number) => {
    setSaveError('');
    if (!(await excludeSignalWiring(osmId))) setSaveError('No se pudo quitar — reintenta');
  };

  const hasBearingOverride = (groupId: number | null) =>
    groupId !== null && groupBearingOverrides.some(d => d.cabinetId === selectedId && d.groupId === groupId);

  const submitBearing = async () => {
    if (activeGroupId === null) return;
    const n = Number(bearingInput);
    if (Number.isNaN(n)) {
      setBearingError('Formato inválido — usa un número, ej. 90');
      return;
    }
    const bearing = ((n % 360) + 360) % 360;
    setBearingError('');
    setSaveError('');
    if (!(await stageBearing({ cabinetId: selectedId, groupId: activeGroupId, bearing }))) {
      setSaveError('No se pudo guardar — reintenta');
      return;
    }
    setRotatingGroupId(null);
  };

  const removeBearing = async () => {
    if (activeGroupId === null) return;
    setSaveError('');
    if (!(await removeBearingOverride(selectedId, activeGroupId))) setSaveError('No se pudo quitar — reintenta');
  };

  const regulatorMasks = alarmMasks.filter(m => m.canonicalId === canonicalId);

  const submitMask = async () => {
    setMaskError('');
    const ok = await createAlarmMask(canonicalId, null, maskNote.trim());
    if (!ok) {
      setMaskError('No se pudo guardar — reintenta');
      return;
    }
    setMaskFormOpen(false);
    setMaskNote('');
  };

  return (
    <motion.div
      initial={{ x: 420, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="absolute top-0 right-0 bottom-0 w-[420px] bg-gray-950/95 backdrop-blur-md
                 border-l border-amber-700/40 z-40 flex flex-col shadow-2xl"
    >
      {/* Cabecera */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <div>
          <div className="text-sm font-semibold text-white font-mono">{toDisplayId(selectedId)}</div>
          <div className="text-[10px] text-amber-500 font-mono">{canonicalId} · Modo Edición</div>
          <a
            href={`/admin/events?regulator_id=${canonicalId}`}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] text-gray-500 hover:text-amber-400 underline"
          >
            Ver historial de cambios ↗
          </a>
        </div>
        <button
          onClick={close}
          className="p-1.5 rounded hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
          title="Cerrar"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {saveError && (
          <div className="text-[11px] text-red-400 bg-red-900/20 border border-red-800/40 rounded p-2">
            {saveError}
          </div>
        )}

        {/* Posición actual */}
        <div>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">
            <MapPin className="w-3 h-3" />Posición actual
          </div>
          <div className="text-[11px] font-mono text-gray-300">
            {fmt(ix.cabinet.position[1])}, {fmt(ix.cabinet.position[0])}
          </div>
          {hasPositionOverride && !pendingCoord && (
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[9px] uppercase tracking-wide text-amber-500">corrección guardada</span>
              <button onClick={removePosition} disabled={positionSaving} className="text-[10px] text-gray-400 hover:text-white underline disabled:opacity-50 disabled:cursor-wait">
                Volver a la posición original
              </button>
            </div>
          )}
        </div>

        {!picking && !pendingCoord && !enteringCoords && (
          <div className="flex gap-2">
            <button
              onClick={startPicking}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded text-[11px] font-medium
                         bg-amber-700/30 text-amber-300 border border-amber-700/50 hover:bg-amber-700/50 transition-colors"
            >
              <Move className="w-3.5 h-3.5" />Reposicionar
            </button>
            <button
              onClick={() => {
                setLatInput(fmt(ix.cabinet.position[1]));
                setLngInput(fmt(ix.cabinet.position[0]));
                setCoordError('');
                setEnteringCoords(true);
              }}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded text-[11px] font-medium
                         bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700 transition-colors"
            >
              Coordenadas exactas
            </button>
          </div>
        )}

        {picking && (
          <div className="text-[11px] text-amber-300 bg-amber-900/20 border border-amber-700/40 rounded p-2.5">
            Click en el mapa para elegir la nueva posición · Esc cancela
          </div>
        )}

        {enteringCoords && !pendingCoord && (
          <div className="space-y-2">
            <div>
              <label className="text-[10px] text-gray-500">Latitud</label>
              <input
                type="text"
                inputMode="decimal"
                value={latInput}
                onChange={e => setLatInput(e.target.value)}
                className="w-full bg-gray-800 text-gray-100 font-mono text-[11px] px-2 py-1.5 rounded border border-gray-700 focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-500">Longitud</label>
              <input
                type="text"
                inputMode="decimal"
                value={lngInput}
                onChange={e => setLngInput(e.target.value)}
                className="w-full bg-gray-800 text-gray-100 font-mono text-[11px] px-2 py-1.5 rounded border border-gray-700 focus:border-amber-500 focus:outline-none"
              />
            </div>
            {coordError && <div className="text-[11px] text-red-400">{coordError}</div>}
            <div className="flex gap-2">
              <button
                onClick={() => setEnteringCoords(false)}
                className="flex-1 px-3 py-1.5 rounded text-xs font-medium bg-gray-800 text-gray-300
                           hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={submitCoords}
                className="flex-1 px-3 py-1.5 rounded text-xs font-medium bg-amber-600 text-white
                           hover:bg-amber-500 transition-colors"
              >
                Usar estas coordenadas
              </button>
            </div>
          </div>
        )}

        {pendingCoord && (
          <div className="space-y-2.5">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Nueva posición</div>
              <div className="text-[11px] font-mono text-amber-300">
                {fmt(pendingCoord[1])}, {fmt(pendingCoord[0])}
              </div>
              {pendingCoordDistance !== null && pendingCoordDistance > 300 && (
                <div className="text-[10px] text-red-400 mt-1">
                  ⚠ A {Math.round(pendingCoordDistance)} m de la posición actual — confirma que es correcto
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 text-[11px] text-gray-300">
              <input
                type="checkbox"
                checked={moveCenter}
                onChange={e => setMoveCenter(e.target.checked)}
                className="accent-amber-500"
              />
              Centrar el mapa en la nueva posición
            </label>
            <div className="flex gap-2">
              <button
                onClick={clearPendingCoord}
                className="flex-1 px-3 py-1.5 rounded text-xs font-medium bg-gray-800 text-gray-300
                           hover:bg-gray-700 transition-colors"
              >
                Descartar
              </button>
              <button
                onClick={confirmMove}
                disabled={positionSaving}
                className="flex-1 px-3 py-1.5 rounded text-xs font-medium bg-amber-600 text-white
                           hover:bg-amber-500 transition-colors disabled:opacity-50 disabled:cursor-wait"
              >
                {positionSaving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        )}

        {/* Cablear grupos */}
        <div className="pt-4 border-t border-gray-800 space-y-2.5">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-wide text-gray-500">
            <span className="flex items-center gap-1.5"><Link2 className="w-3 h-3" />Cablear grupos</span>
            <span className="font-mono normal-case text-gray-600">{cableadosCount}/{ix.groups.length}</span>
          </div>

          {/* Vista general — una celda por grupo, rellena si tiene ≥1
              semáforo asignado. Click selecciona el grupo activo para el
              panel de detalle de abajo. */}
          <div className="grid grid-cols-8 gap-1">
            {ix.groups.map(g => {
              const count = cabinetAssignments.filter(a => a.groupId === g.id).length;
              const isActive = g.id === activeGroupId;
              return (
                <button
                  key={g.id}
                  onClick={() => setSelectedGroupId(g.id)}
                  className={`relative h-9 rounded text-[11px] font-mono font-medium border transition-colors ${
                    count > 0
                      ? 'bg-amber-700/40 text-amber-200 border-amber-600/60'
                      : 'bg-gray-800/60 text-gray-500 border-gray-700'
                  } ${isActive ? 'ring-1 ring-white/70' : 'hover:border-amber-600/60'}`}
                  title={`G${g.id}`}
                >
                  {g.id}
                  {count > 1 && (
                    <span className="absolute -top-1.5 -right-1.5 text-[8px] leading-none bg-amber-500 text-gray-950 rounded-full w-3.5 h-3.5 flex items-center justify-center font-bold">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Detalle del grupo seleccionado en el grid de arriba. */}
          {activeGroup && (
            <div className="rounded border border-gray-800 bg-gray-900/40 p-2.5 text-[11px]">
              <div className="text-gray-300 mb-1.5">{activeGroup.label}</div>
              {activeAssignments.length === 0 ? (
                <span className="text-gray-500 font-mono">sin semáforo</span>
              ) : (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {activeAssignments.map(a => (
                    <span key={a.osmId} className="inline-flex items-center gap-1 text-gray-400 font-mono">
                      #{a.osmId}
                      <button
                        onClick={() => unassignSignal(a.osmId)}
                        disabled={wiringSaving}
                        title="Este semáforo no es de este grupo"
                        className="text-gray-600 hover:text-red-400 disabled:opacity-50 disabled:cursor-wait"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Rotación del grupo activo (ARS-06b) — bearing sintético,
                  sin dato de campo real; corregible a mano hasta que ARS93
                  entregue la topología real. */}
              <div className="pt-2 mt-2 border-t border-gray-800/60">
                {rotatingGroupId === activeGroup.id ? (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number"
                        value={bearingInput}
                        onChange={e => setBearingInput(e.target.value)}
                        className="w-16 bg-gray-800 text-gray-100 font-mono text-[11px] px-2 py-1 rounded border border-gray-700 focus:border-amber-500 focus:outline-none"
                      />
                      <span className="text-gray-500">°</span>
                      <button
                        onClick={() => setBearingInput(String((((Number(bearingInput) || 0) - 90) % 360 + 360) % 360))}
                        className="px-1.5 py-1 rounded text-[10px] font-mono bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700"
                      >
                        -90°
                      </button>
                      <button
                        onClick={() => setBearingInput(String((((Number(bearingInput) || 0) + 90) % 360 + 360) % 360))}
                        className="px-1.5 py-1 rounded text-[10px] font-mono bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700"
                      >
                        +90°
                      </button>
                    </div>
                    {bearingError && <div className="text-red-400">{bearingError}</div>}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setRotatingGroupId(null)}
                        className="flex-1 px-2 py-1 rounded text-[10px] font-medium bg-gray-800 text-gray-300 hover:bg-gray-700"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={submitBearing}
                        disabled={bearingSaving}
                        className="flex-1 px-2 py-1 rounded text-[10px] font-medium bg-amber-600 text-white hover:bg-amber-500 disabled:opacity-50 disabled:cursor-wait"
                      >
                        {bearingSaving ? 'Guardando…' : 'Guardar'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Navigation
                      className="w-3 h-3 text-gray-500"
                      style={{ transform: `rotate(${activeGroup.bearing}deg)` }}
                    />
                    <span className="text-gray-400 font-mono">{activeGroup.bearing}°</span>
                    <button
                      onClick={() => {
                        setBearingInput(String(activeGroup.bearing));
                        setBearingError('');
                        setRotatingGroupId(activeGroup.id);
                      }}
                      className="text-gray-400 hover:text-white underline"
                    >
                      Ajustar rotación
                    </button>
                    {hasBearingOverride(activeGroup.id) && (
                      <>
                        <span className="text-[9px] uppercase tracking-wide text-amber-500">corregida</span>
                        <button
                          onClick={removeBearing}
                          disabled={bearingSaving}
                          className="text-gray-400 hover:text-white underline disabled:opacity-50 disabled:cursor-wait"
                        >
                          Restablecer original
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {!pickingSignal && pendingSignalId === null && (
            <button
              onClick={startPickingSignal}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded text-[11px] font-medium
                         bg-amber-700/30 text-amber-300 border border-amber-700/50 hover:bg-amber-700/50 transition-colors"
            >
              <Link2 className="w-3.5 h-3.5" />Asignar semáforo
            </button>
          )}

          {pickingSignal && (
            <div className="text-[11px] text-amber-300 bg-amber-900/20 border border-amber-700/40 rounded p-2.5">
              Click en un semáforo del mapa · Esc cancela
            </div>
          )}

          {pendingSignalId !== null && (
            <div className="space-y-2">
              <div className="text-[11px] text-gray-300">
                Semáforo <span className="font-mono text-amber-300">#{pendingSignalId}</span>
                {pendingSignalDistance !== null && (
                  <span className="text-gray-500">
                    {' '}· {Math.round(pendingSignalDistance)} m del regulador
                  </span>
                )}
              </div>
              <div className="text-[10px] text-gray-500">¿A qué grupo pertenece? — ordenado por cercanía</div>
              <div className="grid grid-cols-3 gap-1.5 max-h-64 overflow-y-auto pr-1">
                {groupsByDistance.map(g => {
                  const isWired = wiredGroupIds.has(g.id);
                  const dist = pendingSignal ? Math.round(approxDistanceM(pendingSignal.position, g.position)) : null;
                  return (
                    <button
                      key={g.id}
                      onClick={() => confirmSignal(g.id)}
                      disabled={wiringSaving}
                      className={`px-2 py-1.5 rounded text-[11px] font-medium border transition-colors disabled:opacity-50 disabled:cursor-wait ${
                        isWired
                          ? 'bg-amber-900/20 text-amber-300 border-amber-700/40 hover:bg-amber-700/40 hover:border-amber-600'
                          : 'bg-gray-800 text-gray-200 border-gray-700 hover:bg-amber-700/40 hover:border-amber-600'
                      }`}
                    >
                      {g.label}
                      {dist !== null && <span className="block text-[9px] text-gray-500 font-mono">{dist} m</span>}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={clearPendingSignalId}
                className="w-full px-3 py-1.5 rounded text-xs font-medium bg-gray-800 text-gray-300
                           hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
            </div>
          )}
        </div>

        {/* Enmascarar alarmas (ARS-09) — regla compartida con todos los operadores */}
        <div className="pt-4 border-t border-gray-800 space-y-2.5">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500">
            <BellOff className="w-3 h-3" />Enmascarar alarmas
          </div>

          {regulatorMasks.length === 0 && !maskFormOpen && (
            <div className="text-[11px] text-gray-500">Sin enmascarados activos</div>
          )}

          {regulatorMasks.map(m => (
            <div key={m.id} className="rounded border border-gray-700 bg-gray-900/50 p-2 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[11px] text-gray-300">
                  {m.code ? `Código ${m.code}` : 'Todas las alarmas'}
                </div>
                {m.note && <div className="text-[10px] text-gray-500 truncate">{m.note}</div>}
              </div>
              <button
                onClick={() => deleteAlarmMask(m.id)}
                className="text-[11px] text-gray-400 hover:text-red-400 underline flex-shrink-0"
              >
                Quitar
              </button>
            </div>
          ))}

          {!maskFormOpen && (
            <button
              onClick={() => setMaskFormOpen(true)}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded text-[11px] font-medium
                         bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700 transition-colors"
            >
              <BellOff className="w-3.5 h-3.5" />Enmascarar este regulador
            </button>
          )}

          {maskFormOpen && (
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Motivo (opcional, ej. obras hasta 30/9)"
                value={maskNote}
                onChange={e => setMaskNote(e.target.value)}
                className="w-full bg-gray-800 text-gray-100 text-[11px] px-2 py-1.5 rounded border border-gray-700 focus:border-amber-500 focus:outline-none"
              />
              {maskError && <div className="text-[11px] text-red-400">{maskError}</div>}
              <div className="flex gap-2">
                <button
                  onClick={() => { setMaskFormOpen(false); setMaskError(''); }}
                  className="flex-1 px-3 py-1.5 rounded text-xs font-medium bg-gray-800 text-gray-300
                             hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={submitMask}
                  className="flex-1 px-3 py-1.5 rounded text-xs font-medium bg-amber-600 text-white
                             hover:bg-amber-500 transition-colors"
                >
                  Enmascarar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Eliminar regulador (ARS-05) */}
        <div className="pt-4 border-t border-gray-800 space-y-2.5">
          {!confirmingDelete ? (
            <button
              onClick={() => setConfirmingDelete(true)}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded text-[11px] font-medium
                         bg-gray-800 text-red-400 border border-gray-700 hover:bg-red-900/20 hover:border-red-800 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />Eliminar este regulador
            </button>
          ) : (
            <div className="space-y-2">
              <div className="text-[11px] text-red-400">
                Se quita del dataset de inmediato. ¿Seguro?
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmingDelete(false)}
                  className="flex-1 px-3 py-1.5 rounded text-xs font-medium bg-gray-800 text-gray-300
                             hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={deleteRegulator}
                  disabled={lifecycleSaving}
                  className="flex-1 px-3 py-1.5 rounded text-xs font-medium bg-red-700 text-white
                             hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-wait"
                >
                  {lifecycleSaving ? 'Eliminando…' : 'Confirmar'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
